import { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ModalPortal } from '@/components/ui/Modal/ModalPortal';
import {
  ArrowLeft,
  Building2,
  Database,
  ExternalLink,
  RefreshCw,
  Trash2,
  Edit,
  Users,
  Loader2,
  AlertTriangle,
  CreditCard,
  Server,
  Globe,
  Link as LinkIcon,
} from 'lucide-react';
import EditTenantModal from './EditTenantModal';
import TenantUsersPanel from './TenantUsersPanel';

interface TenantUser {
  id: string;
  userEmail: string;
  roleId: string;
  isActive: boolean;
  createdAt: string;
}

interface Plan {
  id: string;
  name: string;
  description: string | null;
  maxUsers: number;
  maxChannels: number;
  priceMonthly: number;
  features: string[];
  integrations: string[];
  isActive: boolean;
}

interface Tenant {
  id: string;
  slug: string;
  name: string;
  rut: string;
  dbSchema: string;
  isActive: boolean;
  trialEndsAt: string | null;
  createdAt: string;
  tenantUsers: TenantUser[];
  planId: string | null;
  plan?: Plan | null;
  hostingType: 'shared' | 'dedicated';
  externalUrl: string | null;
  externalApiUrl: string | null;
  externalDbHost: string | null;
  externalDbPort: number | null;
  externalDbName: string | null;
  externalDbUser: string | null;
}

export default function TenantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const fetchTenant = useCallback(async (tenantId: string) => {
    try {
      const res = await fetch(`/api/super-admin/tenants/${tenantId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
        },
      });
      const data = await res.json();
      setTenant(data.data);
    } catch (error) {
      console.error('Error fetching tenant:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (id) {
      fetchTenant(id);
    }
  }, [id, fetchTenant]);

  const handleSwitchToTenant = async () => {
    if (!tenant) return;

    try {
      const res = await fetch(`/api/super-admin/switch/${tenant.slug}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
        },
      });
      const data = await res.json();

      localStorage.setItem('tenant_token', data.access_token);
      localStorage.setItem('tenant_info', JSON.stringify(data.tenant));

      window.open(`https://${tenant.slug}.lyrax.cl`, '_blank');
    } catch (error) {
      console.error('Error switching to tenant:', error);
    }
  };

  const handleSeedDemo = async () => {
    if (!tenant) return;

    if (!confirm(`¿Crear datos demo para "${tenant.name}"?`)) return;

    try {
      await fetch(`/api/super-admin/tenants/${tenant.id}/seed-demo`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
        },
      });
      alert('Datos demo creados exitosamente');
    } catch (error) {
      console.error('Error seeding demo:', error);
      alert('Error al crear datos demo');
    }
  };

  const handleDeleteTenant = async () => {
    if (!tenant) return;

    setIsDeleting(true);
    try {
      const res = await fetch(`/api/super-admin/tenants/${tenant.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
        },
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || 'Error al eliminar tenant');
      }

      navigate('/tenants');
    } catch (error: any) {
      alert(error.message || 'Error al eliminar tenant');
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#233554] border-t-[#229E64] rounded-full animate-spin" />
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="text-center py-12">
        <p className="text-lyrax-text-secondary">Tenant no encontrado</p>
        <Link to="/tenants" className="text-[#229E64] hover:underline mt-2 inline-block">
          Volver a tenants
        </Link>
      </div>
    );
  }

  return (
    <div>
      <Link
        to="/tenants"
        className="inline-flex items-center gap-2 text-lyrax-text-secondary hover:text-lyrax-primary mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Volver a tenants
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="card">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-[#229E64]/10 rounded-xl flex items-center justify-center">
                  <Building2 className="w-7 h-7 text-[#229E64]" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-lyrax-text">{tenant.name}</h1>
                  <p className="text-lyrax-text-secondary">{tenant.slug}.lyrax.cl</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${
                    tenant.isActive
                      ? 'bg-[#22C55E]/10 text-[#22C55E]'
                      : 'bg-lyrax-surface-hover text-lyrax-text-secondary'
                  }`}
                >
                  {tenant.isActive ? 'Activo' : 'Inactivo'}
                </span>
                <button
                  onClick={() => setIsEditModalOpen(true)}
                  className="p-2 text-lyrax-text-secondary hover:text-lyrax-primary hover:bg-lyrax-surface-hover rounded-lg transition-colors"
                  title="Editar tenant"
                >
                  <Edit className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-lyrax-surface-hover rounded-lg">
                <p className="text-xs text-lyrax-text-secondary mb-1">RUT</p>
                <p className="font-medium text-lyrax-text">{tenant.rut}</p>
              </div>
              <div className="p-4 bg-lyrax-surface-hover rounded-lg">
                <p className="text-xs text-lyrax-text-secondary mb-1">Hosting</p>
                <div className="flex items-center gap-1">
                  {tenant.hostingType === 'shared' ? (
                    <Server className="w-3 h-3 text-lyrax-primary" />
                  ) : (
                    <Globe className="w-3 h-3 text-purple-500" />
                  )}
                  <span className="font-medium text-lyrax-text capitalize">
                    {tenant.hostingType === 'shared' ? 'Compartido' : 'Dedicado'}
                  </span>
                </div>
              </div>
              <div className="p-4 bg-lyrax-surface-hover rounded-lg">
                <p className="text-xs text-lyrax-text-secondary mb-1">Plan</p>
                <p className="font-medium text-lyrax-text flex items-center gap-1">
                  <CreditCard className="w-3 h-3" />
                  {tenant.plan?.name || 'Sin plan'}
                </p>
              </div>
              <div className="p-4 bg-lyrax-surface-hover rounded-lg">
                <p className="text-xs text-lyrax-text-secondary mb-1">Creado</p>
                <p className="font-medium text-lyrax-text">
                  {new Date(tenant.createdAt).toLocaleDateString('es-CL')}
                </p>
              </div>
              {tenant.hostingType === 'dedicated' && tenant.externalUrl && (
                <div className="p-4 bg-purple-500/5 border border-purple-500/20 rounded-lg col-span-2">
                  <p className="text-xs text-purple-400 mb-1">Frontend del cliente</p>
                  <a
                    href={tenant.externalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-purple-400 flex items-center gap-1 hover:underline"
                  >
                    <LinkIcon className="w-3 h-3" />
                    {tenant.externalUrl}
                  </a>
                </div>
              )}
              {tenant.hostingType === 'dedicated' && (tenant as any).externalApiUrl && (
                <div className="p-4 bg-purple-500/5 border border-purple-500/20 rounded-lg col-span-2">
                  <p className="text-xs text-purple-400 mb-1">API del cliente</p>
                  <a
                    href={(tenant as any).externalApiUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-purple-400 flex items-center gap-1 hover:underline"
                  >
                    <LinkIcon className="w-3 h-3" />
                    {(tenant as any).externalApiUrl}
                  </a>
                </div>
              )}
              {tenant.hostingType === 'dedicated' && tenant.externalDbHost && (
                <div className="p-4 bg-purple-500/5 border border-purple-500/20 rounded-lg col-span-2">
                  <p className="text-xs text-purple-400 mb-1 flex items-center gap-1">
                    <Database className="w-3 h-3" />
                    Base de datos externa
                  </p>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div>
                      <p className="text-xs text-purple-400/70">Host</p>
                      <p className="font-medium text-purple-300 text-sm">{tenant.externalDbHost}</p>
                    </div>
                    <div>
                      <p className="text-xs text-purple-400/70">Puerto</p>
                      <p className="font-medium text-purple-300 text-sm">{tenant.externalDbPort || '5432'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-purple-400/70">Base de datos</p>
                      <p className="font-medium text-purple-300 text-sm">{tenant.externalDbName}</p>
                    </div>
                    <div>
                      <p className="text-xs text-purple-400/70">Usuario</p>
                      <p className="font-medium text-purple-300 text-sm">{tenant.externalDbUser}</p>
                    </div>
                  </div>
                </div>
              )}
              {tenant.trialEndsAt && (
                <div className="p-4 bg-lyrax-surface-hover rounded-lg">
                  <p className="text-xs text-lyrax-text-secondary mb-1">Trial termina</p>
                  <p className="font-medium text-lyrax-text">
                    {new Date(tenant.trialEndsAt).toLocaleDateString('es-CL')}
                  </p>
                </div>
              )}
            </div>
          </div>

          <TenantUsersPanel
            tenantId={tenant.id}
            users={tenant.tenantUsers || []}
            userCount={tenant.tenantUsers?.length || 0}
            onUsersChange={() => fetchTenant(tenant.id)}
          />
        </div>

        <div className="space-y-6">
          <div className="card">
            <h3 className="text-lg font-semibold text-lyrax-text mb-4">Acciones</h3>
            <div className="space-y-3">
              {tenant.hostingType === 'shared' ? (
                <button
                  onClick={handleSwitchToTenant}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  Acceder como Admin
                </button>
              ) : (
                <a
                  href={tenant.externalUrl || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary w-full flex items-center justify-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  Abrir Servidor
                </a>
              )}

              {tenant.hostingType === 'shared' && (
                <button
                  onClick={handleSeedDemo}
                  className="btn-secondary w-full flex items-center justify-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Crear Datos Demo
                </button>
              )}

              {tenant.hostingType === 'dedicated' && (
                <div className="text-xs text-lyrax-text-secondary text-center py-2">
                  Datos demo no disponible para servidores dedicados
                </div>
              )}

              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 text-red-400 border border-red-400/30 rounded-lg hover:bg-red-500/10 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Eliminar Tenant
              </button>
            </div>
          </div>

          <div className="card">
            <h3 className="text-lg font-semibold text-lyrax-text mb-4">Estadísticas</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-lyrax-text-secondary">
                  <Users className="w-4 h-4" />
                  <span className="text-sm">Usuarios</span>
                </div>
                <span className="font-semibold text-lyrax-text">
                  {tenant.tenantUsers?.length || 0}
                </span>
              </div>
              {tenant.hostingType === 'shared' && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-lyrax-text-secondary">
                    <Database className="w-4 h-4" />
                    <span className="text-sm">Schema</span>
                  </div>
                  <span className="font-medium text-lyrax-text text-sm font-mono">
                    {tenant.dbSchema}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-lyrax-text-secondary">
                  {tenant.hostingType === 'shared' ? (
                    <Server className="w-4 h-4" />
                  ) : (
                    <Globe className="w-4 h-4" />
                  )}
                  <span className="text-sm">Hosting</span>
                </div>
                <span className="font-medium text-lyrax-text text-sm capitalize">
                  {tenant.hostingType === 'shared' ? 'Compartido' : 'Dedicado'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <EditTenantModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSuccess={() => fetchTenant(tenant.id)}
        tenant={{
          id: tenant.id,
          slug: tenant.slug,
          name: tenant.name,
          rut: tenant.rut,
          isActive: tenant.isActive,
          trialEndsAt: tenant.trialEndsAt,
          planId: tenant.planId,
          hostingType: tenant.hostingType,
          externalUrl: tenant.externalUrl,
          externalApiUrl: (tenant as any).externalApiUrl,
          externalDbHost: tenant.externalDbHost,
          externalDbPort: tenant.externalDbPort,
          externalDbName: tenant.externalDbName,
          externalDbUser: tenant.externalDbUser,
          externalDbPassword: null,
          channels: (tenant as any).channels || [],
          premiumFeatures: (tenant as any).premiumFeatures || [],
        }}
      />

      {showDeleteConfirm && (
        <ModalPortal>
        <div
          className="modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowDeleteConfirm(false);
          }}
        >
          <div className="modal-panel max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-500/10 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-500" />
              </div>
              <h2 className="text-xl font-bold text-lyrax-text">Eliminar Tenant</h2>
            </div>

            <p className="text-lyrax-text-secondary mb-6">
              ¿Estás seguro de que deseas eliminar el tenant <strong>{tenant.name}</strong>? Esta
              acción eliminará el schema de base de datos <strong>{tenant.dbSchema}</strong> y todos
              los datos asociados. Esta acción no se puede deshacer.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="btn-secondary flex-1"
                disabled={isDeleting}
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteTenant}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                disabled={isDeleting}
              >
                {isDeleting && <Loader2 className="w-4 h-4 animate-spin" />}
                {isDeleting ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
        </ModalPortal>
      )}
    </div>
  );
}
